import type { ElementTag } from '~/components/layout/element/element';
import type {
  Component,
  ComponentConfig,
  ComponentType,
  Element,
  IElement,
  LayoutChild,
  TemplateReference,
} from '~/config/types';

export function createElement<T extends ElementTag>(config: IElement<T>) {
  return config;
}

export function createComponent<T extends ComponentType, Config extends ComponentConfig<T>>(config: Config) {
  return config;
}

export function createTemplateRef(config: TemplateReference) {
  return config;
}

let wrapperCounter = 0;
export function createWrapper<T extends ElementTag>(wrapperFactory: (outletId: string) => IElement<T>) {
  return function createElement<T extends ElementTag>(config: IElement<T>) {
    const outletId = `outlet-${(++wrapperCounter * Math.pow(10, 9)).toString(32).substring(2, 10)}`;
    const wrapper = wrapperFactory(outletId);

    traverseChild(wrapper, (_path, el, controller) => {
      if (isElement(el) && el.elementProps.id === outletId) {
        el.children = [config];
        controller.abort();
      }
    });

    return wrapper;
  };
}

export function traverseChild(
  target: LayoutChild,
  cb: (path: number[], element: LayoutChild, controller: AbortController) => void,
  path = [] as number[],
  controller = new AbortController()
) {
  cb(path, target, controller);

  if (Array.isArray(target.children)) {
    target.children.forEach((child, index) => {
      if (!controller.signal.aborted) {
        traverseChild(child, cb, [...path, index], controller);
      }
    });
  }
}

export function isElement(child: LayoutChild): child is Element {
  return 'elementProps' in child;
}

export function isTemplateReference(child: LayoutChild): child is TemplateReference {
  return 'template' in child;
}

export function isComponent(child: LayoutChild): child is Component {
  return 'component' in child;
}
